import React, { useEffect } from "react";

import {
	NavigationHeaderGeneric,
	NavigationFooter,
	headerTransformRebrand,
	footerTransformRebrand,
} from "@website-builder/ui/shared";
import { Grid, Col } from "@website-builder/ui/shared/elements";
import {
	B2U_REBRAND_PHASED_ROLLOUT_VWO_COOKIE,
	NavigationFooterPaid,
} from "@website-builder/ui/b2u";
import Components, { ComponentTransform } from "./../../../../components";

import {
	addToDataLayer,
	getCookie,
	getEssentialData,
	hbsptEventHandler,
	triggerVWOSegmentEvents,
	triggerAppFinishedSegmentEvents,
} from "@website-builder/utilities-index";

import { QueryClientProvider } from "react-query";
import { queryClient } from "@website-builder/utilities-index";

import { UniversityWrapper } from "./style";

const RebrandBlogIndexLayout = ({
	story,
	basicConfig = {},
	token,
	storyList = undefined,
}) => {
	const segmentProps = {
		typeFormLink: story?.content?.typeFormLink,
		courseSlug: story?.content?.courseSlug,
	};
	let preHerobannerPadding = [72, 72];
	// You can add data that are set as page properties .This will be passed as context to all the components
	let context = getEssentialData(story);
	let header = null;
	let footer = null;
	let isRebrandPage = true;
	// VWO's traffic split cookie used for phased rollout of UP Rebrand
	let isRebrandFlow = false;
	let isPaidRebrandFooter = false;
	let featuredPosts = [];
	let hideNoIndexPosts = false;

	if (token) {
		context.token = token;
	}

	useEffect(() => {
		isRebrandFlow = getCookie(B2U_REBRAND_PHASED_ROLLOUT_VWO_COOKIE) === "2";

		const eventListenerCallback = (event) => {
			hbsptEventHandler(event, {
				is_rebrand_page: isRebrandPage,
				is_rebrand_flow: isRebrandFlow,
			});
		};

		window.addEventListener("message", eventListenerCallback);
		triggerVWOSegmentEvents();
		triggerAppFinishedSegmentEvents({ ...segmentProps });
		pushAnalyticsData();

		return () => {
			window.removeEventListener("message", eventListenerCallback);
		};
	}, [isRebrandPage]);

	const pushAnalyticsData = () => {
		addToDataLayer({
			page_workshop_id: story?.content?.workshopId,
			page_full_slug: story?.full_slug,
			is_rebrand_page: isRebrandPage,
			is_rebrand_flow: isRebrandFlow,
		});
	};

	try {
		featuredPosts = story?.content?.featuredPosts || [];
		hideNoIndexPosts = story?.content?.hideNoIndexPosts || false;
		const headerInput = story?.content?.layout?.content?.header[0];
		const footerInput = story?.content?.layout?.content?.footer[0];
		const {
			isHeaderSticky = false,
			isHeaderLight = false,
			navHeaderCta: headerCTA = [],
			stickyHeader = [],
		} = story?.content;
		context.isHeaderSticky = isHeaderSticky;
		if (story?.content?.stickyHeader?.[0]) {
			preHerobannerPadding[1] += 60;
		}
		if (!headerInput?.hidePreNav) {
			preHerobannerPadding[0] += 32;
		}
		if (headerInput) {
			header = headerTransformRebrand({
				...headerInput,
				stickyHeader: stickyHeader?.[0],
				isHeaderSticky,
				isHeaderLight,
				headerCTA,
			});
		}
		if (footerInput) {
			isPaidRebrandFooter = footerInput?.component === "navigationFooterPaid";
			if (isPaidRebrandFooter) {
				footer = footerInput;
			} else {
				footer = footerTransformRebrand(footerInput);
			}
		}
	} catch (error) {
		console.error(error);
	}

	const BlogCategoriesWR = Components["BlogCategoriesWR"];
	const BlogCategoriesWRTransform = ComponentTransform["BlogCategoriesWR"];
	const LatestPosts = Components["LatestPosts"];
	const LatestPostsTransform = ComponentTransform["LatestPosts"];
	const FeaturedPosts = Components["FeaturedPosts"];
	const FeaturedPostsTransform = ComponentTransform["FeaturedPosts"];
	const BlogBannerWR = Components["BlogBannerWR"];
	const BlogBannerWRTransform = ComponentTransform["BlogBannerWR"];

	const bannerPostRaw = story?.content?.bannerPost;
	const bannerTransformProps = {
		coverImage: bannerPostRaw?.content?.coverImage,
		title: bannerPostRaw?.content?.title,
		featuredDescription: bannerPostRaw?.content?.featuredDescription,
		dateOfPublishing:
			bannerPostRaw?.content?.dateOfPublishing || bannerPostRaw?.published_at,
		isIndexPage: true,
		slug: bannerPostRaw?.full_slug,
		bannerImageSizes: { default: { height: 300 } },
	};

	return (
		<QueryClientProvider client={queryClient}>
			<UniversityWrapper preHeroBannerPadding={preHerobannerPadding}>
				{header?.isGenericHeader ? (
					<NavigationHeaderGeneric
						{...header}
						context={context}
						basicConfig={basicConfig}
					/>
				) : null}
				<main className="main-wrapper">
					<div className="rebrand-section">
						<Grid>
							<Col span={3} mdSpan={6}>
								{/* Search Bar */}
								<BlogCategoriesWR
									context={context}
									hideNoIndexPosts={hideNoIndexPosts}
									{...BlogCategoriesWRTransform(storyList)}
								></BlogCategoriesWR>
							</Col>
							<Col span={9} mdSpan={6}>
								{bannerPostRaw ? (
									<BlogBannerWR
										{...BlogBannerWRTransform(bannerTransformProps)}
									></BlogBannerWR>
								) : null}
								{featuredPosts.length ? (
									<FeaturedPosts
										{...FeaturedPostsTransform(featuredPosts)}
									></FeaturedPosts>
								) : null}
								<LatestPosts
									context={context}
									{...LatestPostsTransform(storyList, hideNoIndexPosts)}
								></LatestPosts>
							</Col>
						</Grid>
					</div>
				</main>
				{isPaidRebrandFooter ? (
					<NavigationFooterPaid
						className="paid-rebrand-footer"
						{...footer}
						context={context}
						basicConfig={basicConfig}
					/>
				) : (
					<NavigationFooter
						className="rebrand-footer"
						{...footer}
						context={context}
						basicConfig={basicConfig}
					/>
				)}
			</UniversityWrapper>
		</QueryClientProvider>
	);
};

export default RebrandBlogIndexLayout;
